import { useCallback, useEffect, useState } from 'react'

import { useNotifications } from '@joor/design-system'
import type { Option } from '@joor/design-system'

import type { RetailerProfile } from '../retailerProfilePage.types'
import { useValidateTaxId } from '../useValidateTaxId'
import { taxIdProValidCountries } from './taxIdProValidCountries'
import type { TaxIdFieldErrors } from './taxIdVerification.types'

/** `taxIdCountry` from profile is an ISO code (e.g. `es` / `ES`). */
function taxIdCountryOptionFromIso(
  taxIdCountry?: string | null,
): Option | null {
  if (!taxIdCountry?.trim()) return null
  const n = taxIdCountry.trim().toLowerCase()
  return taxIdProValidCountries.find((o) => o.value.toLowerCase() === n) ?? null
}

type VerifyRunResult =
  | { ok: true; normalizedTaxId?: string }
  | { ok: false; errors: TaxIdFieldErrors; apiMessage?: string }

type TaxIdVerifyForSaveResult =
  | { ok: true; taxId: string; taxIdCountry: string }
  | { ok: false }

export function useTaxIdFields(retailerProfile?: RetailerProfile | null) {
  const { addToasts } = useNotifications()
  const { validateTaxId, validating } = useValidateTaxId()
  const [countryOption, setCountryOption] = useState<Option | null>(null)
  const [taxId, setTaxId] = useState('')
  const [errors, setErrors] = useState<TaxIdFieldErrors>({})
  const [taxIdVerified, setTaxIdVerified] = useState(false)

  const clearTaxIdVerified = useCallback(() => setTaxIdVerified(false), [])

  // it initializes once the id exists, and it resets if id changes
  useEffect(() => {
    if (!retailerProfile?.id) return
    const taxId = retailerProfile.taxId?.trim() ?? ''
    const taxIdCountry = retailerProfile.taxIdCountry?.trim() ?? ''
    setTaxId(taxId)
    setCountryOption(taxIdCountryOptionFromIso(taxIdCountry || null))
    setTaxIdVerified(Boolean(taxId && taxIdCountry))
    setErrors({})
  }, [retailerProfile?.id])

  const runVerification = useCallback(
    async (snapshot?: {
      countryOption: Option | null
      taxId: string
    }): Promise<VerifyRunResult> => {
      const effectiveCountryOption = snapshot?.countryOption ?? countryOption
      const effectiveTaxId = snapshot !== undefined ? snapshot.taxId : taxId

      const hasCountry = Boolean(effectiveCountryOption?.value)
      const hasTax = Boolean(effectiveTaxId.trim())

      if (!hasCountry && !hasTax) {
        setErrors({})
        setTaxIdVerified(false)
        return { ok: true }
      }

      if (!hasCountry) {
        const err = { country: 'Select a country.' }
        setErrors(err)
        setTaxIdVerified(false)
        return { ok: false, errors: err }
      }

      if (!hasTax) {
        setErrors((prev) => {
          const next = { ...prev }
          delete next.taxId
          return next
        })
        setTaxIdVerified(false)
        return { ok: true }
      }

      const trimmedTaxId = effectiveTaxId.trim()
      const apiResult = await validateTaxId(
        effectiveCountryOption!.value,
        trimmedTaxId,
      )

      if (apiResult.status === 'valid') {
        setErrors({})
        setTaxIdVerified(true)
        return { ok: true, normalizedTaxId: trimmedTaxId }
      }

      const message = apiResult.message
      const fieldErrors = { taxId: message }
      setErrors(fieldErrors)
      setTaxIdVerified(false)
      return {
        ok: false,
        errors: fieldErrors,
        apiMessage: message,
      }
    },
    [countryOption, taxId, validateTaxId],
  )

  const handleCountrySelect = useCallback(
    ({ selected }: { selected: Option }) => {
      setCountryOption(selected)
      clearTaxIdVerified()
      setErrors((prev) => {
        const next = { ...prev }
        delete next.country
        return next
      })
      void runVerification({ countryOption: selected, taxId })
    },
    [clearTaxIdVerified, runVerification, taxId],
  )

  const handleTaxIdChange = useCallback(
    (value: string) => {
      setTaxId(value)
      clearTaxIdVerified()
      setErrors((prev) => {
        const next = { ...prev }
        delete next.taxId
        return next
      })
    },
    [clearTaxIdVerified],
  )

  const verifyOnBlur = useCallback(() => {
    void runVerification()
  }, [runVerification])

  /** Validates country + Tax ID via API. On failure, sets field errors and may show a verify error toast. */
  const verifyAndNormalizeForSave =
    useCallback(async (): Promise<TaxIdVerifyForSaveResult> => {
      const trimmedTaxId = taxId.trim()

      if (!trimmedTaxId) {
        setErrors({})
        return { ok: true, taxId: '', taxIdCountry: '' }
      }

      if (!countryOption?.value) {
        await runVerification({ countryOption, taxId })
        return { ok: false }
      }

      const verificationResult = await runVerification({
        countryOption,
        taxId,
      })

      if (!verificationResult.ok) {
        if (verificationResult.apiMessage) {
          addToasts([
            {
              type: 'error',
              title: 'Error Verifying',
              description: verificationResult.apiMessage,
            },
          ])
        }
        return { ok: false }
      }

      const normalizedTaxId = (
        verificationResult.normalizedTaxId ?? trimmedTaxId
      ).trim()

      if (!countryOption.value || !normalizedTaxId) {
        return { ok: false }
      }

      return {
        ok: true,
        taxId: normalizedTaxId,
        taxIdCountry: countryOption.value,
      }
    }, [runVerification, countryOption, taxId, addToasts])

  return {
    countryOption,
    taxId,
    errors,
    validating,
    taxIdVerified,
    handleCountrySelect,
    handleTaxIdChange,
    verifyOnBlur,
    verifyAndNormalizeForSave,
  }
}
